import { ethers } from 'ethers';
import { provider, CACHE_DURATION } from '../config/constants';
import { withTimeout, sleep } from '../utils/helpers';

// Position Manager Addresses
export const CAMELOT_POSITION_MANAGER = '0x00c7f3082833e796A5b3e4Bd59f6642FF44DCD15';
export const UNISWAP_POSITION_MANAGER = '0xC36442b4a4522E871399CD717aBDD847Ab11FE88';

// Multicall3 Address (deployed on Arbitrum)
export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';

// Camelot Position Manager ABI
const CAMELOT_POSITION_ABI = [
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'positions',
    outputs: [
      { internalType: 'uint96', name: 'nonce', type: 'uint96' },
      { internalType: 'address', name: 'operator', type: 'address' },
      { internalType: 'address', name: 'token0', type: 'address' },
      { internalType: 'address', name: 'token1', type: 'address' },
      { internalType: 'int24', name: 'tickLower', type: 'int24' },
      { internalType: 'int24', name: 'tickUpper', type: 'int24' },
      { internalType: 'uint128', name: 'liquidity', type: 'uint128' },
      { internalType: 'uint256', name: 'feeGrowthInside0LastX128', type: 'uint256' },
      { internalType: 'uint256', name: 'feeGrowthInside1LastX128', type: 'uint256' },
      { internalType: 'uint128', name: 'tokensOwed0', type: 'uint128' },
      { internalType: 'uint128', name: 'tokensOwed1', type: 'uint128' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'ownerOf',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
];

// Uniswap V3 Position Manager ABI
const UNISWAP_POSITION_ABI = [
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'positions',
    outputs: [
      { internalType: 'uint96', name: 'nonce', type: 'uint96' },
      { internalType: 'address', name: 'operator', type: 'address' },
      { internalType: 'address', name: 'token0', type: 'address' },
      { internalType: 'address', name: 'token1', type: 'address' },
      { internalType: 'uint24', name: 'fee', type: 'uint24' },
      { internalType: 'int24', name: 'tickLower', type: 'int24' },
      { internalType: 'int24', name: 'tickUpper', type: 'int24' },
      { internalType: 'uint128', name: 'liquidity', type: 'uint128' },
      { internalType: 'uint256', name: 'feeGrowthInside0LastX128', type: 'uint256' },
      { internalType: 'uint256', name: 'feeGrowthInside1LastX128', type: 'uint256' },
      { internalType: 'uint128', name: 'tokensOwed0', type: 'uint128' },
      { internalType: 'uint128', name: 'tokensOwed1', type: 'uint128' },
    ],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'uint256', name: 'tokenId', type: 'uint256' }],
    name: 'ownerOf',
    outputs: [{ internalType: 'address', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
];

// Pool ABI common
const POOL_ABI = [
  'function globalState() view returns (uint160 price, int24 tick, uint16 feeZto, uint16 feeOtz, uint16 timepointIndex, uint8 communityFeeToken0, uint8 communityFeeToken1, bool unlocked)',
  'function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)',
  'function tickSpacing() view returns (int24)',
  'function liquidity() view returns (uint128)',
  'function fee() view returns (uint24)',
  'function token0() view returns (address)',
  'function token1() view returns (address)',
];

export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)',
];

const MULTICALL3_ABI = [
  {
    inputs: [
      {
        components: [
          { name: 'target', type: 'address' },
          { name: 'callData', type: 'bytes' },
        ],
        name: 'calls',
        type: 'tuple[]',
      },
    ],
    name: 'aggregate',
    outputs: [
      { name: 'blockNumber', type: 'uint256' },
      { name: 'returnData', type: 'bytes[]' },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
];

const camelotPositionManager = new ethers.Contract(CAMELOT_POSITION_MANAGER, CAMELOT_POSITION_ABI, provider);
const uniswapPositionManager = new ethers.Contract(UNISWAP_POSITION_MANAGER, UNISWAP_POSITION_ABI, provider);
const multicall3Contract = new ethers.Contract(MULTICALL3_ADDRESS, MULTICALL3_ABI, provider);

// Caches
const poolStateCache = {};
const positionCache = {};
const poolValidationCache = {}; // Cache pool validation results

export async function isValidPoolContract(poolAddress) {
  try {
    if (!ethers.utils.isAddress(poolAddress)) return false;

    // Check cache first to avoid redundant RPC calls
    if (poolValidationCache[poolAddress] !== undefined) {
      return poolValidationCache[poolAddress];
    }

    const contract = new ethers.Contract(poolAddress, POOL_ABI, provider);
    const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000));
    const validationPromise = Promise.all([contract.token0().catch(() => null), contract.token1().catch(() => null)]);
    const [token0, token1] = await Promise.race([validationPromise, timeout]);
    const isValid = token0 && token1 && ethers.utils.isAddress(token0) && ethers.utils.isAddress(token1);

    poolValidationCache[poolAddress] = isValid;
    return isValid;
  } catch {
    poolValidationCache[poolAddress] = false;
    return false;
  }
}

/**
 * Batch fetch pool states using Multicall3
 * Reduces 3-4 RPC calls per pool to 1 Multicall batch for all pools
 */
export async function batchGetPoolStates(poolAddresses) {
  const results = {};
  const uncachedPools = [];

  // Check cache first
  for (const poolAddress of poolAddresses) {
    if (poolStateCache[poolAddress] && Date.now() - poolStateCache[poolAddress].timestamp < CACHE_DURATION) {
      results[poolAddress] = poolStateCache[poolAddress].data;
      continue;
    }

    // Check session storage
    try {
      const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
      const cached = JSON.parse(sessionStorage.getItem(ssKey) || 'null');
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        poolStateCache[poolAddress] = { data: cached.data, timestamp: cached.timestamp };
        results[poolAddress] = cached.data;
        continue;
      }
    } catch {}

    uncachedPools.push(poolAddress);
  }

  if (uncachedPools.length === 0) return results;

  // Build multicall for all pools (try Uniswap format first)
  const calls = [];
  for (const poolAddress of uncachedPools) {
    const contract = new ethers.Contract(poolAddress, POOL_ABI, provider);

    // Batch: slot0, tickSpacing, liquidity, fee
    calls.push(
      { target: poolAddress, callData: contract.interface.encodeFunctionData('slot0'), type: 'slot0', pool: poolAddress },
      { target: poolAddress, callData: contract.interface.encodeFunctionData('tickSpacing'), type: 'tickSpacing', pool: poolAddress },
      { target: poolAddress, callData: contract.interface.encodeFunctionData('liquidity'), type: 'liquidity', pool: poolAddress },
      { target: poolAddress, callData: contract.interface.encodeFunctionData('fee'), type: 'fee', pool: poolAddress }
    );
  }

  try {
    const [, returnData] = await multicall3Contract.aggregate(calls.map(c => ({ target: c.target, callData: c.callData })));

    // Group results by pool
    const poolData = {};
    returnData.forEach((data, index) => {
      const call = calls[index];
      const contract = new ethers.Contract(call.pool, POOL_ABI, provider);

      try {
        if (!poolData[call.pool]) poolData[call.pool] = {};

        if (call.type === 'slot0') {
          const decoded = contract.interface.decodeFunctionResult('slot0', data);
          poolData[call.pool].slot0 = decoded;
        } else if (call.type === 'tickSpacing') {
          poolData[call.pool].tickSpacing = contract.interface.decodeFunctionResult('tickSpacing', data)[0];
        } else if (call.type === 'liquidity') {
          poolData[call.pool].liquidity = contract.interface.decodeFunctionResult('liquidity', data)[0];
        } else if (call.type === 'fee') {
          poolData[call.pool].fee = contract.interface.decodeFunctionResult('fee', data)[0];
        }
      } catch (err) {
        // Ignore individual decode errors
      }
    });

    // Process results
    for (const [poolAddress, data] of Object.entries(poolData)) {
      try {
        if (data.slot0) {
          const poolState = {
            sqrtPriceX96: data.slot0.sqrtPriceX96.toString(),
            tickCurrent: data.slot0.tick,
            tickSpacing: data.tickSpacing || 60,
            poolLiquidity: (data.liquidity || ethers.BigNumber.from('0')).toString(),
            fee: data.fee || 3000,
            protocol: 'uniswap',
          };
          results[poolAddress] = poolState;
          poolStateCache[poolAddress] = { data: poolState, timestamp: Date.now() };

          try {
            const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
            sessionStorage.setItem(ssKey, JSON.stringify({ data: poolState, timestamp: Date.now() }));
          } catch {}
        }
      } catch (err) {
        // Try Camelot format for failed pools
      }
    }

    // Handle failed pools individually (might be Camelot pools)
    for (const poolAddress of uncachedPools) {
      if (!results[poolAddress]) {
        results[poolAddress] = await getPoolState(poolAddress);
      }
    }
  } catch (err) {
    console.warn('Batch pool state fetch failed, falling back to individual calls:', err.message);
    // Fallback to individual calls
    for (const poolAddress of uncachedPools) {
      results[poolAddress] = await getPoolState(poolAddress);
    }
  }

  return results;
}

export async function getPoolState(poolAddress) {
  if (poolStateCache[poolAddress] && Date.now() - poolStateCache[poolAddress].timestamp < CACHE_DURATION) {
    return poolStateCache[poolAddress].data;
  }
  // Session cache (browser) for pool states
  try {
    const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
    const cached = JSON.parse(sessionStorage.getItem(ssKey) || 'null');
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      poolStateCache[poolAddress] = { data: cached.data, timestamp: cached.timestamp };
      return cached.data;
    }
  } catch {}
  const maxRetries = 3;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // OPTIMIZATION: Skip validation check and directly try fetching pool state
      // If pool is invalid, the slot0/globalState call will fail naturally
      // This saves 2 RPC calls (token0, token1) per pool
      const contract = new ethers.Contract(poolAddress, POOL_ABI, provider);

      try {
        const [slot0, spacing, poolLiquidity] = await Promise.all([
          withTimeout(contract.slot0()),
          withTimeout(contract.tickSpacing()).catch(() => 60),
          withTimeout(contract.liquidity()).catch(() => ethers.BigNumber.from('0')),
        ]);
        let fee;
        try {
          fee = await withTimeout(contract.fee());
        } catch {
          fee = 3000;
        }
        const poolState = {
          sqrtPriceX96: slot0.sqrtPriceX96.toString(),
          tickCurrent: slot0.tick,
          tickSpacing: spacing,
          poolLiquidity: poolLiquidity.toString(),
          fee,
          protocol: 'uniswap',
        };
        poolStateCache[poolAddress] = { data: poolState, timestamp: Date.now() };
        try {
          const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
          sessionStorage.setItem(ssKey, JSON.stringify({ data: poolState, timestamp: Date.now() }));
        } catch {}
        return poolState;
      } catch {
        const [state, spacing, poolLiquidity] = await Promise.all([
          withTimeout(contract.globalState()),
          withTimeout(contract.tickSpacing()).catch(() => 60),
          withTimeout(contract.liquidity()).catch(() => ethers.BigNumber.from('0')),
        ]);
        let fee;
        try {
          fee = await withTimeout(contract.fee());
        } catch {
          fee = 500;
        }
        const poolState = {
          sqrtPriceX96: state.price.toString(),
          tickCurrent: state.tick,
          tickSpacing: spacing,
          poolLiquidity: poolLiquidity.toString(),
          fee,
          protocol: 'camelot',
        };
        poolStateCache[poolAddress] = { data: poolState, timestamp: Date.now() };
        try {
          const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
          sessionStorage.setItem(ssKey, JSON.stringify({ data: poolState, timestamp: Date.now() }));
        } catch {}
        return poolState;
      }
    } catch (e) {
      if (attempt === maxRetries - 1) {
        poolStateCache[poolAddress] = { data: null, timestamp: Date.now() };
        try {
          const ssKey = `meowfi_pool_${poolAddress.toLowerCase()}`;
          sessionStorage.setItem(ssKey, JSON.stringify({ data: null, timestamp: Date.now() }));
        } catch {}
        return null;
      }
      await sleep(1000 * Math.pow(2, attempt));
    }
  }
  return null;
}

/**
 * Optimized position fetching that tries both protocols in parallel
 * Reduces 2 sequential failed attempts to 1 parallel attempt
 */
export async function fetchPositionWithRetry(nftId, maxRetries = 3) {
  if (positionCache[nftId]) return positionCache[nftId];

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Try both protocols in parallel instead of sequential
      const [camelotResult, uniswapResult] = await Promise.allSettled([
        camelotPositionManager.positions(nftId).catch(() => null),
        uniswapPositionManager.positions(nftId).catch(() => null),
      ]);

      // Check Camelot result
      if (camelotResult.status === 'fulfilled' && camelotResult.value) {
        const camelotData = camelotResult.value;
        if (camelotData.token0 !== ethers.constants.AddressZero) {
          const positionData = {
            nftId,
            nonce: camelotData.nonce,
            operator: camelotData.operator,
            token0: camelotData.token0,
            token1: camelotData.token1,
            fee: null,
            tickLower: camelotData.tickLower,
            tickUpper: camelotData.tickUpper,
            liquidity: camelotData.liquidity,
            feeGrowthInside0LastX128: camelotData.feeGrowthInside0LastX128,
            feeGrowthInside1LastX128: camelotData.feeGrowthInside1LastX128,
            tokensOwed0: camelotData.tokensOwed0,
            tokensOwed1: camelotData.tokensOwed1,
            protocol: 'camelot',
          };
          positionCache[nftId] = positionData;
          return positionData;
        }
      }

      // Check Uniswap result
      if (uniswapResult.status === 'fulfilled' && uniswapResult.value) {
        const uniswapData = uniswapResult.value;
        if (uniswapData.token0 !== ethers.constants.AddressZero) {
          const positionData = {
            nftId,
            nonce: uniswapData.nonce,
            operator: uniswapData.operator,
            token0: uniswapData.token0,
            token1: uniswapData.token1,
            fee: uniswapData.fee,
            tickLower: uniswapData.tickLower,
            tickUpper: uniswapData.tickUpper,
            liquidity: uniswapData.liquidity,
            feeGrowthInside0LastX128: uniswapData.feeGrowthInside0LastX128,
            feeGrowthInside1LastX128: uniswapData.feeGrowthInside1LastX128,
            tokensOwed0: uniswapData.tokensOwed0,
            tokensOwed1: uniswapData.tokensOwed1,
            protocol: 'uniswap',
          };
          positionCache[nftId] = positionData;
          return positionData;
        }
      }

      if (attempt < maxRetries - 1) await sleep(1000 * (attempt + 1));
    } catch {
      if (attempt === maxRetries - 1) return null;
      await sleep(1000 * (attempt + 1));
    }
  }
  return null;
}

export async function fetchAllPositions(allNftIds) {
  const positions = {};
  const batchSize = 50;
  const maxConcurrency = 30; // further increased concurrency for faster fetch

  for (let i = 0; i < allNftIds.length; i += batchSize) {
    const batch = allNftIds.slice(i, i + batchSize);
    const chunks = [];
    for (let j = 0; j < batch.length; j += maxConcurrency) {
      chunks.push(batch.slice(j, j + maxConcurrency));
    }
    for (const chunk of chunks) {
      const chunkResults = await Promise.all(chunk.map((nftId) => fetchPositionWithRetry(nftId)));
      chunkResults.forEach((positionData, index) => {
        if (positionData) {
          positions[chunk[index]] = positionData;
        }
      });
      await sleep(100); // further reduced delay between chunks
    }
  }
  return positions;
}

/**
 * Fetch wallet balance using Multicall3 for efficient batching
 * Reduces N token balance calls to 1 multicall per wallet
 */
export async function getSmartWalletBalanceUSD(walletAddress, allPrices, addressToTokenInfo) {
  let totalUSD = 0;

  const entries = Object.entries(addressToTokenInfo)
    // Skip tokens with zero price to avoid unnecessary RPC calls
    .filter(([addr]) => (allPrices[addr.toLowerCase()]?.usd || 0) > 0);

  try {
    // Build multicall requests - separate ERC20 tokens from ETH
    const calls = [];
    const erc20Tokens = [];

    // Fetch ETH balance separately (cannot be part of multicall)
    const ethEntry = entries.find(([, info]) => info.symbol === 'ETH');
    if (ethEntry) {
      try {
        const [addr, tokenInfo] = ethEntry;
        const ethBalance = await provider.getBalance(walletAddress);
        const normalized = parseFloat(ethers.utils.formatUnits(ethBalance, tokenInfo.decimals));
        const usdValue = normalized * (allPrices[addr.toLowerCase()]?.usd || 0);
        if (usdValue > 0) totalUSD += usdValue;
      } catch (err) {
        console.warn(`Failed to fetch ETH balance:`, err.message);
      }
    }

    // Build multicall for all ERC20 tokens
    for (const [addr, tokenInfo] of entries) {
      if (tokenInfo.symbol !== 'ETH') {
        const tokenContract = new ethers.Contract(addr, ERC20_ABI, provider);
        const callData = tokenContract.interface.encodeFunctionData('balanceOf', [walletAddress]);
        calls.push({ target: addr, callData });
        erc20Tokens.push({ addr, tokenInfo });
      }
    }

    // Execute multicall for all ERC20 tokens in a single RPC call
    if (calls.length > 0) {
      const [, returnData] = await multicall3Contract.aggregate(calls);

      // Decode results - index matches erc20Tokens array
      returnData.forEach((data, index) => {
        try {
          const { addr, tokenInfo } = erc20Tokens[index];
          const balance = ethers.BigNumber.from(data);
          const normalized = parseFloat(ethers.utils.formatUnits(balance, tokenInfo.decimals));
          const usdValue = normalized * (allPrices[addr.toLowerCase()]?.usd || 0);

          if (usdValue > 0) {
            totalUSD += usdValue;
          }
        } catch (err) {
          console.warn(`Failed to decode balance for ${erc20Tokens[index]?.tokenInfo?.symbol}:`, err.message);
        }
      });
    }
  } catch (err) {
    console.warn(`Multicall failed for wallet ${walletAddress}, falling back to individual calls:`, err.message);

    // Fallback to individual calls if multicall fails
    const concurrency = 5;
    let index = 0;
    async function worker() {
      while (index < entries.length) {
        const current = index++;
        const [addr, tokenInfo] = entries[current];
        try {
          let balance;

          if (tokenInfo.symbol === 'ETH') {
            balance = await provider.getBalance(walletAddress);
          } else {
            const tokenContract = new ethers.Contract(addr, ERC20_ABI, provider);
            balance = await tokenContract.balanceOf(walletAddress);
          }

          const decimals = tokenInfo.decimals;
          const normalized = parseFloat(ethers.utils.formatUnits(balance, decimals));
          const usdValue = normalized * (allPrices[addr.toLowerCase()]?.usd || 0);

          if (usdValue > 0) {
            totalUSD += usdValue;
          }
        } catch (err) {
          console.warn(`Failed to fetch balance for ${tokenInfo.symbol}:`, err.message);
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, entries.length) }, () => worker()));
  }

  return totalUSD;
}


const UNI_NPM_ABI = [
  'function collect((uint256 tokenId,address recipient,uint128 amount0Max,uint128 amount1Max)) returns (uint256 amount0, uint256 amount1)'
];
const ALG_NPM_ABI = [
  'function collect(uint256 tokenId,address recipient,uint128 amount0Max,uint128 amount1Max) returns (uint256 amount0, uint256 amount1)'
];
const MAX_UINT128 = ethers.BigNumber.from(2).pow(128).sub(1);

// Small cache for simulateCollect to avoid duplicate RPC in a run
const simCollectCache = new Map(); // key: `${pm}:${wallet}:${id}` -> { res: {amount0,amount1}, ts }
const SIM_CACHE_TTL_MS = 30_000;



export async function simulateCollectFees(positionManager, walletAddress, tokenId) {
  const key = `${positionManager.toLowerCase()}:${(walletAddress || '').toLowerCase()}:${String(tokenId)}`;
  const cached = simCollectCache.get(key);
  if (cached && Date.now() - cached.ts < SIM_CACHE_TTL_MS) {
    return cached.res;
  }

  // 1) Uniswap signature
  try {
    const cUni = new ethers.Contract(positionManager, UNI_NPM_ABI, provider);
    const res = await cUni.callStatic.collect(
      {
        tokenId: ethers.BigNumber.from(String(tokenId)),
        recipient: walletAddress,
        amount0Max: MAX_UINT128,
        amount1Max: MAX_UINT128
      },
      { from: walletAddress }
    );
    const out = {
      amount0: ethers.BigNumber.from(res.amount0 ?? res[0]),
      amount1: ethers.BigNumber.from(res.amount1 ?? res[1])
    };
    simCollectCache.set(key, { res: out, ts: Date.now() });
    return out;
  } catch (_) {
    // 2) Algebra/Camelot signature
    try {
      const cAlg = new ethers.Contract(positionManager, ALG_NPM_ABI, provider);
      const res2 = await cAlg.callStatic.collect(
        ethers.BigNumber.from(String(tokenId)),
        walletAddress,
        MAX_UINT128,
        MAX_UINT128,
        { from: walletAddress }
      );
      const out = {
        amount0: ethers.BigNumber.from(res2.amount0 ?? res2[0]),
        amount1: ethers.BigNumber.from(res2.amount1 ?? res2[1])
      };
      simCollectCache.set(key, { res: out, ts: Date.now() });
      return out;
    } catch {
      const out = { amount0: ethers.constants.Zero, amount1: ethers.constants.Zero };
      simCollectCache.set(key, { res: out, ts: Date.now() });
      return out;
    }
  }
}

/**
 * Batch simulate collect fees for multiple positions
 * Returns a Map of nftId -> {amount0, amount1}
 */
export async function batchSimulateCollectFees(requests) {
  // requests: Array<{positionManager, walletAddress, tokenId}>
  const results = new Map();
  const uncached = [];

  // Check cache first
  for (const req of requests) {
    const key = `${req.positionManager.toLowerCase()}:${(req.walletAddress || '').toLowerCase()}:${String(req.tokenId)}`;
    const cached = simCollectCache.get(key);
    if (cached && Date.now() - cached.ts < SIM_CACHE_TTL_MS) {
      results.set(req.tokenId, cached.res);
    } else {
      uncached.push(req);
    }
  }

  // Batch fetch uncached
  const concurrency = 10;
  for (let i = 0; i < uncached.length; i += concurrency) {
    const batch = uncached.slice(i, i + concurrency);
    const batchResults = await Promise.all(
      batch.map(req =>
        simulateCollectFees(req.positionManager, req.walletAddress, req.tokenId)
          .then(res => ({ tokenId: req.tokenId, res }))
          .catch(() => ({ tokenId: req.tokenId, res: { amount0: ethers.constants.Zero, amount1: ethers.constants.Zero } }))
      )
    );
    batchResults.forEach(({ tokenId, res }) => results.set(tokenId, res));
  }

  return results;
}

export { POOL_ABI };
